import React, { useEffect, useMemo, useState } from "react";
import { GrAscend, GrDescend } from "react-icons/gr";
import { PiMagnifyingGlass } from "react-icons/pi";
import { TbReload } from "react-icons/tb";
import SidebarModelsLoading from "../Loaders/SidebarModelsLoading";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const AiChatSidebarRight = ({
    show,
    handleClose,
    setSelectedModelTitle,
    selectedModelTitle,
    setSelectedModel,
    selectedModel,
    setProviderModel
}) => {
    const {t} = useLanguage()
    const [data, setData] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");

    const fetchData = async () => {
        setLoadingData(true);

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_models`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    limit: 20
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            const sorted = (result.data || []).sort((a, b) => {
                const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
                const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;

                return aTime - aTime; // newest → oldest
            });

            setData(sorted);
        } catch (err) {
            setLoadingData(false);
            console.error("Error fetching models:", err);
        } finally {
            setTimeout(() => {
                setLoadingData(false);
            }, 1000);
        }
    };

    const filteredAndSortedData = useMemo(() => {
        if (!data) return [];

        const term = searchTerm.toLowerCase();

        let result = data.filter((model) => {
            if (!term) return true;

            const searchInObject = (obj) => {
                return Object.values(obj).some((value) => {
                    if (value === null || value === undefined) return false;

                    if (typeof value === "string" || typeof value === "number") {
                        return String(value).toLowerCase().includes(term);
                    }

                    if (typeof value === "boolean") {
                        return false;
                    }

                    if (typeof value === "object") {
                        return searchInObject(value);
                    }

                    return false;
                });
            };

            return searchInObject(model);
        });

        result.sort((a, b) => {
            const aTime = Date.parse(a.created_at || 0);
            const bTime = Date.parse(b.created_at || 0);

            return sortOrder === "asc"
                ? aTime - bTime
                : bTime - aTime;
        });

        return result;
    }, [data, searchTerm, sortOrder]);

    const setItems = (item) => {
        setSelectedModel(item)
        setSelectedModelTitle(item.e8_slug);
        setProviderModel(item.inference_provider)
        localStorage.setItem("ai_selected_model_title", item.e8_slug);
        localStorage.setItem("ai_selected_model", JSON.stringify(item));
    }

    const closeSidebar = () => {
        handleClose()
        setSearchTerm("")
    }

    useEffect(() => {
        fetchData();
    }, [show]);

    return (
        <div className={`sidebar_right_cont ${show ? "sidebar_right_cont_show" : ""}`}>
            <div className="sidebar_right_close" onClick={closeSidebar}></div>
            <div className="sidebar_right_main">
                <div className="sidebar_right_main_header">
                    <p>{t.playground.choose_llm_title}</p>
                    <button onClick={closeSidebar}><i className="fa-solid fa-x"></i></button>
                </div>
                <div className="sidebar_right_main_filter_cont ">
                    <div className="sidebar_right_main_filter_input_cont fade-in">
                        <input
                            className="filter_input"
                            placeholder={t.search_filter}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="filter_input_icon"><PiMagnifyingGlass /></span>
                    </div>
                    <div className="sidebar_right_main_filter_btn_cont fade-in">
                        {data.length > 1 && (
                            <>
                                <button
                                    className={`sidebar_right_main_filter_btn fade-in ${sortOrder === "asc" ? "active" : ""}`}
                                    onClick={() => setSortOrder("asc")} title="A-Z"
                                >
                                    <GrAscend />
                                </button>
                                <button
                                    className={`sidebar_right_main_filter_btn fade-in ${sortOrder === "desc" ? "active" : ""}`}
                                    onClick={() => setSortOrder("desc")}
                                    title="Z-A"
                                >
                                    <GrDescend />
                                </button>
                            </>
                        )}
                        <button
                            className="sidebar_right_main_filter_btn fade-in"
                            onClick={() => fetchData()}
                            title={t.reload}
                        >
                            <TbReload />
                        </button>
                    </div>
                </div>
                {loadingData ? (
                    <SidebarModelsLoading />
                ) : (
                    <div className="sidebar_right_main_body">
                        {filteredAndSortedData.length ? (
                            filteredAndSortedData.map((item) => (
                                <div key={item.id} className={`sidebar_right_main_body_item fade-in ${selectedModelTitle == item.e8_slug ? "sidebar_right_main_body_item_selected" : ""}`} onClick={() => setItems(item)}>
                                    <div>
                                        <p className="sidebar_right_main_body_item_title">{item.name} <span className="sidebar_right_main_body_item_title_provider">{item.inference_provider}</span></p>
                                        <p className="sidebar_right_main_body_item_description">{item.description}</p>
                                    </div>
                                    <div className="sidebar_right_main_body_item_price_cont">
                                        <span className="sidebar_right_main_body_item_price_item">Input ${((item.input_price_per_1m_tokens / 1_000_000_000) + ((item.input_price_per_1m_tokens / 1_000_000_000) * 0.10)).toFixed(2)} per 1mln tokens</span>
                                        <span className="sidebar_right_main_body_item_price_item">Output ${((item.output_price_per_1m_tokens / 1_000_000_000) + ((item.output_price_per_1m_tokens / 1_000_000_000) * 0.10)).toFixed(2)} per 1mln tokens</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="sidebar_right_main_empty fade-in">
                                <p>{t.no_data_found}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AiChatSidebarRight;